import { useState, useEffect, createContext, useContext } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) throw new Error("useAdminAuth must be used within AdminLayout");
  return context;
};

export default function AdminLayout() {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const verifyAdmin = async () => {
      const token = localStorage.getItem("admin_token");
      if (!token) {
        navigate("/admin/login");
        return;
      }

      try {
        const response = await axios.get(`${API}/admin/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAdmin(response.data);
      } catch (error) {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
        toast.error("Admin session expired");
        navigate("/admin/login");
      } finally {
        setLoading(false);
      }
    };

    verifyAdmin();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    toast.success("Logged out");
    navigate("/admin/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  const navigation = [
    { name: "Overview", path: "/admin/overview", icon: "ChartBarIcon" },
    { name: "Users", path: "/admin/users", icon: "UsersIcon" },
    { name: "Billing", path: "/admin/billing", icon: "CreditCardIcon" },
    { name: "Pricing Control", path: "/admin/pricing", icon: "TagIcon" },
    { name: "Sales Leads", path: "/admin/sales-leads", icon: "UsersIcon" },
    { name: "Organizations", path: "/admin/orgs", icon: "BuildingIcon" },
    { name: "Promotions", path: "/admin/promotions", icon: "TagIcon" },
    { name: "Credits & Usage", path: "/admin/credits", icon: "CurrencyDollarIcon" },
    { name: "Blog", path: "/admin/blog", icon: "DocumentTextIcon" },
    { name: "AI Blog", path: "/admin/blog/ai", icon: "SparklesIcon" },
    { name: "Traffic", path: "/admin/traffic", icon: "GlobeAltIcon" },
    { name: "Analytics", path: "/admin/analytics", icon: "ChartPieIcon" },
    { name: "Audit Log", path: "/admin/audit", icon: "ClipboardDocumentListIcon" },
    { name: "Contracts", path: "/admin/contracts", icon: "DocumentTextIcon" },
    { name: "Q&A Monitor", path: "/admin/qa", icon: "ChatBubbleLeftRightIcon" },
    { name: "Case Review", path: "/admin/case-review", icon: "BriefcaseIcon" },
    { name: "API Keys", path: "/admin/api-keys", icon: "KeyIcon" },
    { name: "Settings", path: "/admin/settings", icon: "Cog6ToothIcon" },
    { name: "Support Tools", path: "/admin/support", icon: "LifebuoyIcon" },
    { name: "Admin Team", path: "/admin/team", icon: "ShieldCheckIcon" },
    // Contract Expert Section
    { name: "---Contract Expert", path: "", icon: "" },
    { name: "Pilot Dashboard", path: "/admin/pilot-dashboard", icon: "RocketLaunchIcon" },
    { name: "Agent Analytics", path: "/admin/agent-analytics", icon: "SparklesIcon" },
    { name: "Customer Success", path: "/admin/customer-success", icon: "HeartIcon" },
    { name: "Agent Support", path: "/admin/agent-support", icon: "WrenchScrewdriverIcon" },
    // SaaS Intelligence Section
    { name: "---SaaS Intelligence", path: "", icon: "" },
    { name: "SaaS Overview", path: "/admin/saas-overview", icon: "PresentationChartLineIcon" },
    { name: "Activation", path: "/admin/activation", icon: "RocketLaunchIcon" },
    { name: "Credit Profit", path: "/admin/credit-profitability", icon: "BanknotesIcon" },
    { name: "Churn Risk", path: "/admin/churn", icon: "ExclamationTriangleIcon" },
  ];

  return (
    <AdminAuthContext.Provider value={{ admin, handleLogout }}>
      <div className="min-h-screen bg-background">
        {/* Top Bar */}
        <div className="fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-40">
          <div className="h-full px-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-md hover:bg-muted transition"
              >
                <svg className="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                  <span className="text-primary-foreground font-bold text-lg">A</span>
                </div>
                <div>
                  <h1 className="text-foreground font-semibold text-lg font-heading">CBAIQ Admin</h1>
                  <p className="text-muted-foreground text-xs">Control Center</p>
                </div>
              </div>
            </div>

            {/* Admin User Menu */}
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-foreground font-medium">{admin?.email}</p>
                <p className="text-xs text-muted-foreground capitalize">{admin?.role?.replace('_', ' ')}</p>
              </div>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-md transition text-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div
          className={`fixed top-16 left-0 bottom-0 bg-card border-r border-border transition-all duration-300 z-30 ${
            sidebarOpen ? "w-64" : "w-0"
          } overflow-hidden`}
        >
          <nav className="p-4 space-y-1 overflow-y-auto h-full">
            {navigation.map((item, index) => {
              // Handle divider
              if (item.name.startsWith("---")) {
                const sectionName = item.name.replace("---", "") || "Section";
                return (
                  <div key={`divider-${index}`} className="py-2">
                    <hr className="border-border" />
                    <p className="text-xs text-muted-foreground uppercase mt-2 px-4">{sectionName}</p>
                  </div>
                );
              }
              
              const isActive = location.pathname === item.path || 
                (item.path !== "/admin/overview" && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center justify-between px-4 py-3 rounded-md transition ${
                    isActive
                      ? "bg-primary/10 text-primary border border-primary/20"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  <span className="font-medium">{item.name}</span>
                  {item.badge && (
                    <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        <div
          className={`pt-16 transition-all duration-300 ${
            sidebarOpen ? "ml-64" : "ml-0"
          }`}
        >
          <div className="p-8">
            <Outlet />
          </div>
        </div>
      </div>
    </AdminAuthContext.Provider>
  );
}
